import React, { useState, useRef } from 'react';
import {
  Plus,
  FolderOpen,
  Trash2,
  Copy,
  Search,
  Dumbbell,
  Users,
  User,
  Calendar,
  Sparkles,
  LogOut,
  ChevronDown,
  Download,
  Upload,
  Loader2,
} from 'lucide-react';
import type { Plan, TrainingGoal, TrainingLevel, ProgressionModel } from '../types';
import {
  TRAINING_GOAL_LABELS,
  TRAINING_LEVEL_LABELS,
  PROGRESSION_MODEL_LABELS,
} from '../types';
import { usePlanStore } from '../store/usePlanStore';
import { useAuthStore } from '../store/useAuthStore';
import { PLAN_TEMPLATES } from '../data/templates';

const GOAL_COLORS: Record<string, string> = {
  hypertrophy: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
  strength: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
  power: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
  endurance: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
  general: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
};

export default function PlanDashboard() {
  const { savedPlans, openPlan, deletePlan, duplicatePlan, createPlan, loadPlan, loading, error } = usePlanStore();
  const profile = useAuthStore((s) => s.currentProfile());
  const logout = useAuthStore((s) => s.logout);

  const [search, setSearch] = useState('');
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: 'Nowy program',
    clientName: '',
    goal: 'hypertrophy' as TrainingGoal,
    trainingLevel: 'intermediate' as TrainingLevel,
    mesocycleLength: 4,
    frequency: 4,
    fatigueTolerance: 6,
    progressionModel: 'undulating' as ProgressionModel,
  });

  const filtered = savedPlans.filter((p) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      (p.clientName || '').toLowerCase().includes(q) ||
      TRAINING_GOAL_LABELS[p.goal].toLowerCase().includes(q)
    );
  });

  const handleCreate = () => {
    createPlan(form);
    setShowNewPlan(false);
    setForm({ ...form, name: 'Nowy program', clientName: '' });
  };

  const handleLoadTemplate = (templateId: string) => {
    const t = PLAN_TEMPLATES.find((t) => t.id === templateId);
    if (t) {
      loadPlan(t.factory());
      setShowTemplates(false);
    }
  };

  // ── Export plan to JSON file ──
  const handleExportPlan = (plan: Plan, e: React.MouseEvent) => {
    e.stopPropagation();
    const data = JSON.stringify(plan, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plan.name.replace(/[^a-zA-Z0-9ąęóśłżźćńĄĘÓŚŁŻŹĆŃ _-]/g, '_')}_backup.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Export all plans ──
  const handleExportAll = () => {
    const data = JSON.stringify(savedPlans, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apex_all_plans_backup_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Import plan from JSON file ──
  const handleImportPlan = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        // Support single plan or array of plans
        const plans: Plan[] = Array.isArray(parsed) ? parsed : [parsed];
        let imported = 0;
        for (const plan of plans) {
          if (plan && typeof plan === 'object' && plan.id && plan.name && plan.weeks) {
            loadPlan(plan);
            imported++;
          }
        }
        if (imported === 0) {
          alert('Plik nie zawiera prawidłowych planów treningowych.');
        } else {
          alert(`Zaimportowano ${imported} ${imported === 1 ? 'plan' : imported < 5 ? 'plany' : 'planów'}.`);
        }
      } catch {
        alert('Błąd odczytu pliku. Upewnij się, że to plik JSON z planem treningowym.');
      }
    };
    reader.readAsText(file);
    // Reset input so the same file can be imported again
    event.target.value = '';
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* ── Header ─────────────────────────── */}
      <div className="flex items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FolderOpen size={24} className="text-brand-500" />
            Moje plany treningowe
          </h1>
          {profile && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {profile.role === 'trainer' ? (
                <span className="flex items-center gap-1">
                  <Users size={14} /> Trener: {profile.name}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <User size={14} /> {profile.name}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {profile && (
            <button
              onClick={logout}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
            >
              <LogOut size={14} />
              Wyloguj
            </button>
          )}
        </div>
      </div>

      {/* ── Search + Actions ───────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po nazwie, kliencie, celu..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowNewPlan(!showNewPlan); setShowTemplates(false); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
          >
            <Plus size={16} />
            Nowy plan
          </button>
          <button
            onClick={() => { setShowTemplates(!showTemplates); setShowNewPlan(false); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 text-sm font-medium transition-all bg-white dark:bg-slate-900"
          >
            <Sparkles size={16} className="text-brand-500" />
            Szablony
          </button>
          <button
            onClick={() => importFileRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 text-sm font-medium transition-all bg-white dark:bg-slate-900"
            title="Wczytaj plan z pliku"
          >
            <Upload size={16} className="text-brand-500" />
            <span className="hidden sm:inline">Wczytaj</span>
          </button>
          {savedPlans.length > 0 && (
            <button
              onClick={handleExportAll}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 text-sm font-medium transition-all bg-white dark:bg-slate-900"
              title="Eksportuj wszystkie plany"
            >
              <Download size={16} className="text-slate-500" />
              <span className="hidden sm:inline">Eksport</span>
            </button>
          )}
          <input
            ref={importFileRef}
            type="file"
            accept=".json"
            onChange={handleImportPlan}
            className="hidden"
          />
        </div>
      </div>

      {/* ── Templates ──────────────────────── */}
      {showTemplates && (
        <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Szybki start - Szablony
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PLAN_TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => handleLoadTemplate(t.id)}
                className="text-left p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-brand-500" />
                  <span className="font-semibold text-sm">{t.name}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                  {t.description}
                </p>
                <div className="flex gap-2 mt-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${GOAL_COLORS[t.goal] || ''}`}>
                    {TRAINING_GOAL_LABELS[t.goal]}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                    {t.frequency}×/tydz.
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── New plan form ──────────────────── */}
      {showNewPlan && (
        <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
            Nowy plan treningowy
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Nazwa programu
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Dla kogo? <span className="text-slate-400">(opcjonalne)</span>
                </label>
                <input
                  type="text"
                  value={form.clientName}
                  onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                  placeholder="np. Jan Kowalski, ja, klient A..."
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Cel</label>
                <select
                  value={form.goal}
                  onChange={(e) => setForm({ ...form, goal: e.target.value as TrainingGoal })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(TRAINING_GOAL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Poziom</label>
                <select
                  value={form.trainingLevel}
                  onChange={(e) => setForm({ ...form, trainingLevel: e.target.value as TrainingLevel })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(TRAINING_LEVEL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Progresja</label>
                <select
                  value={form.progressionModel}
                  onChange={(e) => setForm({ ...form, progressionModel: e.target.value as ProgressionModel })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  {Object.entries(PROGRESSION_MODEL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Tygodnie</label>
                <input
                  type="number"
                  min={2}
                  max={12}
                  value={form.mesocycleLength}
                  onChange={(e) => setForm({ ...form, mesocycleLength: parseInt(e.target.value) || 4 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sesji/tydz.</label>
                <input
                  type="number"
                  min={1}
                  max={7}
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: parseInt(e.target.value) || 4 })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Zmęczenie ({form.fatigueTolerance}/10)
                </label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={form.fatigueTolerance}
                  onChange={(e) => setForm({ ...form, fatigueTolerance: parseInt(e.target.value) })}
                  className="w-full accent-brand-500 mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowNewPlan(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                Anuluj
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Stwórz plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Plans grid ─────────────────────── */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 size={32} className="text-brand-600 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Ładowanie planów...</p>
        </div>
      ) : filtered.length === 0 && !showNewPlan && !showTemplates ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
            <Dumbbell size={32} className="text-slate-400" />
          </div>
          <h2 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">
            {savedPlans.length === 0 ? 'Brak planów' : 'Nie znaleziono'}
          </h2>
          <p className="text-sm text-slate-400">
            {savedPlans.length === 0
              ? 'Stwórz pierwszy plan treningowy lub wybierz szablon'
              : 'Spróbuj zmienić wyszukiwanie'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const totalExercises = p.weeks.reduce(
              (sum, w) => sum + w.sessions.reduce((s2, ses) => s2 + ses.exercises.length + (ses.warmupExercises || []).length, 0), 0
            );
            const totalSessions = p.weeks.reduce((sum, w) => sum + w.sessions.length, 0);

            return (
              <div
                key={p.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden hover:border-brand-400 dark:hover:border-brand-500 hover:shadow-lg transition-all group cursor-pointer"
                onClick={() => openPlan(p.id)}
              >
                {/* Card header */}
                <div className="p-4 pb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-sm leading-tight line-clamp-2 group-hover:text-brand-600 transition-colors">
                      {p.name}
                    </h3>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                      GOAL_COLORS[p.goal] || 'bg-slate-100 dark:bg-slate-800 text-slate-600'
                    }`}>
                      {TRAINING_GOAL_LABELS[p.goal]}
                    </span>
                  </div>

                  {p.clientName && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mb-2">
                      <User size={12} />
                      <span>{p.clientName}</span>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
                    <span>{p.weeks.length} tyg.</span>
                    <span>{totalSessions} sesji</span>
                    <span>{totalExercises} ćw.</span>
                    <span>{p.frequency}×/tydz.</span>
                  </div>
                </div>

                {/* Card footer */}
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Calendar size={10} />
                    {formatDate(p.updatedAt)}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleExportPlan(p, e)}
                      className="p-1 rounded text-slate-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
                      title="Eksportuj do pliku"
                    >
                      <Download size={13} />
                    </button>
                    <button
                      onClick={() => duplicatePlan(p.id)}
                      className="p-1 rounded text-slate-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
                      title="Duplikuj"
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Usunąć plan "${p.name}"?`)) deletePlan(p.id);
                      }}
                      className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition"
                      title="Usuń"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats footer */}
      {savedPlans.length > 0 && (
        <div className="text-center text-[10px] text-slate-400 mt-6">
          Łącznie: {savedPlans.length} {savedPlans.length === 1 ? 'plan' : savedPlans.length < 5 ? 'plany' : 'planów'}
          {search && filtered.length !== savedPlans.length && ` (wyświetlono ${filtered.length})`}
        </div>
      )}
    </div>
  );
}
